import { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Loader2, DollarSign, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

interface PayPalFundsProps {
  onSuccess: () => void;
  giftGroupId?: string | null;
  recommendedAmount?: number | null;
}

// PayPal SDK types

interface PayPalOnApproveData {
  orderID: string;
}

interface PayPalButton {
  render: (container: string) => void;
}

declare global {
  interface Window {
    paypal?: {
      Buttons: (config: {
        createOrder: () => Promise<string>;
        onApprove: (data: PayPalOnApproveData) => Promise<void>;
        onError?: (err: Error) => void;
        style?: {
          layout?: string;
          color?: string;
          shape?: string;
          label?: string;
        };
      }) => PayPalButton;
    };
  }
}

export function PayPalFunds({ onSuccess, giftGroupId, recommendedAmount }: PayPalFundsProps) {
  // Determine if user came from a gift group contribution flow
  const isFromContributionFlow = !!(giftGroupId && recommendedAmount);
  
  // Use recommendedAmount if provided, otherwise default to empty string
  const [customAmount, setCustomAmount] = useState<string>(
    recommendedAmount ? recommendedAmount.toString() : ''
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  // Auto-contribute checkbox - only default to true if user explicitly came from contribution flow
  const [autoContribute, setAutoContribute] = useState<boolean>(isFromContributionFlow);
  const autoContributeRef = useRef<boolean>(isFromContributionFlow);
  const paypalButtonsRef = useRef<HTMLDivElement>(null);
  const paypalButtonsRendered = useRef(false);
  const customAmountRef = useRef<string>(recommendedAmount ? recommendedAmount.toString() : '');

  // Production domains that should use live PayPal
  const PRODUCTION_DOMAINS = [
    'bettertogivethanreceive.com',
    'btgtr.com',
    'gifteroni.com',
    'giftholio.com',
    'giftswap.club',
    'givegreatgifts.com',
  ];
  
  // Check if we're on a production domain
  const isProductionDomain = PRODUCTION_DOMAINS.some(domain => 
    window.location.hostname === domain || window.location.hostname.endsWith('.' + domain)
  );
  
  // Use production client ID on production domains, sandbox on preview/development
  const PAYPAL_CLIENT_ID = isProductionDomain 
    ? import.meta.env.VITE_PAYPAL_CLIENT_ID 
    : (import.meta.env.VITE_PAYPAL_CLIENT_ID_SANDBOX || import.meta.env.VITE_PAYPAL_CLIENT_ID);
  
  const isUsingSandbox = !isProductionDomain;
  
  // Only show diagnostic logs in preview/development environments
  const isDevelopment = import.meta.env.MODE !== 'production' || window.location.hostname.includes('pages.dev');
  const debugLog = (...args: unknown[]) => {
    if (isDevelopment) {
      console.log(...args);
    }
  };
  const debugError = (...args: unknown[]) => {
    if (isDevelopment) {
      console.error(...args);
    }
  };

  // Load PayPal SDK
  useEffect(() => {
    debugLog('[PAYPAL-SDK] Checking environment...');
    debugLog('  - Current hostname:', window.location.hostname);
    debugLog('  - Is production domain:', isProductionDomain);
    debugLog('  - VITE_PAYPAL_CLIENT_ID_SANDBOX:', import.meta.env.VITE_PAYPAL_CLIENT_ID_SANDBOX ? 'SET' : 'NOT SET');
    debugLog('  - VITE_PAYPAL_CLIENT_ID:', import.meta.env.VITE_PAYPAL_CLIENT_ID ? 'SET' : 'NOT SET');
    debugLog('  - Selected PAYPAL_CLIENT_ID:', PAYPAL_CLIENT_ID ? `${PAYPAL_CLIENT_ID.substring(0, 10)}...` : 'NOT SET');
    debugLog('  - Using:', isUsingSandbox ? 'SANDBOX' : 'PRODUCTION');
    
    if (!PAYPAL_CLIENT_ID) {
      debugError('❌ PayPal Client ID is missing!');
      debugLog('Available import.meta.env keys:', Object.keys(import.meta.env));
      setSdkError(isProductionDomain 
        ? 'PayPal is not configured for production. Please contact support.' 
        : 'PayPal sandbox is not configured. Please contact support.');
      return;
    }
    
    debugLog('✅ PayPal Client ID found, loading SDK...');

    // Remove old SDK script if it exists (to force reload with new parameters)
    const existingScript = document.querySelector(
      `script[src^="https://www.paypal.com/sdk/js"]`
    );
    if (existingScript) {
      debugLog('🔄 Removing existing PayPal SDK script...');
      existingScript.remove();
      delete window.paypal; // Clear old SDK from window
    }

    // Load PayPal SDK script
    // The client ID determines the environment (sandbox vs production)
    // - Sandbox client IDs route to sandbox.paypal.com
    // - Production client IDs route to www.paypal.com
    // Backend dynamically selects sandbox vs production based on request origin
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD&intent=capture&enable-funding=venmo`;
    script.async = true;
    
    debugLog('[PAYPAL-SDK] Loading PayPal SDK...');
    debugLog('  - SDK URL:', script.src.substring(0, 100) + '...');
    debugLog('  - Mode:', isUsingSandbox ? 'SANDBOX' : 'PRODUCTION');
    debugLog('  - Client ID (first 20 chars):', PAYPAL_CLIENT_ID.substring(0, 20) + '...');
    
    script.onload = () => {
      debugLog('✅ PayPal SDK loaded successfully');
      setSdkLoaded(true);
      paypalButtonsRendered.current = false; // Reset when SDK loads
    };
    
    script.onerror = () => {
      debugError('❌ Failed to load PayPal SDK');
      setSdkError('Failed to load PayPal. Please refresh the page.');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount
      const scriptToRemove = document.querySelector(
        `script[src^="https://www.paypal.com/sdk/js"]`
      );
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [PAYPAL_CLIENT_ID]);

  // Render PayPal buttons when SDK is loaded
  useEffect(() => {
    debugLog('🔵 PayPal buttons render check:', {
      sdkLoaded,
      windowPaypalExists: !!window.paypal,
      buttonRefExists: !!paypalButtonsRef.current,
      alreadyRendered: paypalButtonsRendered.current
    });
    
    if (!sdkLoaded || !window.paypal || !paypalButtonsRef.current) {
      debugLog('⏳ Waiting for PayPal SDK and container...');
      return;
    }

    // Don't re-render if already rendered
    if (paypalButtonsRendered.current) {
      debugLog('ℹ️ PayPal buttons already rendered, skipping...');
      return;
    }

    debugLog('🎨 Rendering PayPal buttons...');
    try {
      const buttons = window.paypal.Buttons({
        createOrder: async () => {
          debugLog('🔵 createOrder called');
          setIsProcessing(true);
          
          // Validate amount when user clicks PayPal button - use ref to get current value
          const selectedAmount = parseFloat(customAmountRef.current);
          debugLog('💰 Amount from ref:', selectedAmount);
          
          if (!selectedAmount || selectedAmount < 1 || selectedAmount > 10000) {
            toast.error('Invalid amount', {
              description: 'Please enter an amount between $1.00 and $10,000.00',
            });
            setIsProcessing(false);
            throw new Error('Invalid amount');
          }
          
          try {
            debugLog('📞 Calling backend createPayPalOrder...');
            // Call backend to create PayPal order
            const response = await api.createPayPalOrder(selectedAmount);
            
            debugLog('✅ Backend response:', response);
            
            if (!response.success || !response.orderId) {
              throw new Error('Failed to create PayPal order');
            }

            toast.success('PayPal order created', {
              description: 'Complete the payment in the popup',
            });

            debugLog('🎫 Returning orderId:', response.orderId);
            return response.orderId;
          } catch (error) {
            debugError('❌ Create order error:', error);
            toast.error('Failed to create payment', {
              description: 'Please try again or contact support',
            });
            setIsProcessing(false);
            throw error;
          }
        },
        onApprove: async (data) => {
          try {
            // Call backend to capture payment
            const response = await api.capturePayPalPayment(data.orderID);

            if (!response.success) {
              throw new Error('Failed to capture payment');
            }

            toast.success('Payment successful!', {
              description: `Added $${response.amount.toFixed(2)} to your balance`,
            });

            // If user opted to auto-contribute to a gift group, do it
            if (giftGroupId && autoContributeRef.current && response.amount) {
              try {
                debugLog('[AUTO-CONTRIB] Attempting auto-contribution to gift group:', giftGroupId, 'amount:', response.amount);
                const contributeResponse = await api.contributeToGiftGroup(giftGroupId, response.amount);
                
                debugLog('[AUTO-CONTRIB] Auto-contribution successful:', contributeResponse);
                toast.success('Contribution complete!', {
                  description: `Successfully contributed $${contributeResponse.contributedAmount.toFixed(2)} to the gift group`,
                  duration: 5000,
                });
              } catch (contributeError) {
                debugError('[AUTO-CONTRIB] Auto-contribute error:', contributeError);
                
                // Provide a user-friendly error message
                const errorMessage = contributeError instanceof Error && contributeError.message 
                  ? contributeError.message 
                  : 'An unexpected error occurred';
                
                toast.error('Contribution failed', {
                  description: `Funds were added to your balance, but automatic contribution failed. ${errorMessage}. Please go to the gift group page to contribute manually.`,
                  duration: 10000, // Show longer so user can read
                });
                
                // Don't navigate away so user can see the error
                setIsProcessing(false);
                return; // Early return - don't call onSuccess()
              }
            }

            // Reset form
            setCustomAmount('');
            customAmountRef.current = '';
            setIsProcessing(false);

            // Notify parent to refresh balance
            onSuccess();
          } catch (error) {
            debugError('Capture payment error:', error);
            toast.error('Payment processing failed', {
              description: 'Please contact support if you were charged',
            });
            setIsProcessing(false);
          }
        },
        onError: (err) => {
          debugError('PayPal button error:', err);
          toast.error('PayPal error', {
            description: 'Please try again or contact support',
          });
          setIsProcessing(false);
        },
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'paypal',
        },
      });

      buttons.render('#paypal-button-container');
      paypalButtonsRendered.current = true;
      debugLog('✅ PayPal buttons rendered successfully');
    } catch (error) {
      debugError('❌ PayPal buttons render error:', error);
      setSdkError('Failed to initialize PayPal buttons');
    }
  }, [sdkLoaded, onSuccess, giftGroupId]); // Removed customAmount from dependencies

  const selectedAmount = parseFloat(customAmount);
  const isValidAmount = selectedAmount >= 1 && selectedAmount <= 10000;

  if (sdkError) {
    return (
      <Card className="p-6 border-destructive/50 bg-destructive/5">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-destructive mt-0.5" />
          <div>
            <div className="text-destructive mb-1 font-semibold">PayPal Unavailable</div>
            <div className="text-sm text-muted-foreground">{sdkError}</div>
            <div className="text-xs text-muted-foreground mt-2">
              Please check the browser console for detailed diagnostics.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 border-primary/20 shadow-lg">
      <div className="mb-4">
        <h3 className="text-primary mb-2">Add Funds via PayPal</h3>
        <p className="text-sm text-muted-foreground">
          {giftGroupId 
            ? 'Add funds to your credit balance. You can choose to contribute to your gift group automatically.'
            : 'Top up your credit balance to contribute to gift groups'
          }
        </p>
      </div>

      {/* Two Column Layout: Amount Input (Left) | PayPal Buttons (Right) */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column: Amount Input */}
        <div className="space-y-4">
          <Label htmlFor="amount-input">
            Amount to Add
          </Label>
          
          {/* Amount Input with currency formatting */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <DollarSign className="w-4 h-4 text-muted-foreground" />
            </div>
            <Input
              id="amount-input"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={customAmount}
              onChange={(e) => {
                // Allow only numbers and decimal point
                let newValue = e.target.value.replace(/[^\d.]/g, '');
                
                // Prevent multiple decimal points
                const parts = newValue.split('.');
                if (parts.length > 2) {
                  newValue = parts[0] + '.' + parts.slice(1).join('');
                }
                
                // Limit to 2 decimal places
                if (parts[1] && parts[1].length > 2) {
                  newValue = parts[0] + '.' + parts[1].slice(0, 2);
                }
                
                setCustomAmount(newValue);
                customAmountRef.current = newValue; // Update ref with current value
              }}
              disabled={isProcessing}
              className="pl-9 text-lg"
            />
          </div>

          {/* Auto-contribute checkbox - only show when giftGroupId is present */}
          {giftGroupId && (
            <div className="flex items-center space-x-2 mt-3">
              <Checkbox
                id="auto-contribute"
                checked={autoContribute}
                onCheckedChange={(checked) => {
                  const newValue = checked === true;
                  setAutoContribute(newValue);
                  autoContributeRef.current = newValue;
                }}
                disabled={isProcessing}
              />
              <label
                htmlFor="auto-contribute"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Automatically contribute to gift group after payment
              </label>
            </div>
          )}

          {/* Gift Group Contribution Notice */}
          {giftGroupId && autoContribute && (
            <p className="text-sm text-primary font-medium mt-2">
              This amount will be automatically contributed to your gift group after payment
            </p>
          )}

          {/* Amount Validation Message */}
          {customAmount && !isValidAmount && (
            <p className="text-sm text-destructive">
              Amount must be between $1.00 and $10,000.00
            </p>
          )}
          
          {/* Helper Text */}
          {!customAmount && !giftGroupId && (
            <p className="text-sm text-muted-foreground">
              Enter an amount to see payment options
            </p>
          )}
        </div>

        {/* Right Column: PayPal Buttons */}
        <div>
          {/* Sandbox Mode Indicator */}
          {isUsingSandbox && (
            <div className="mb-3 px-3 py-2 bg-amber-100 border border-amber-300 rounded-md">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-500 text-white uppercase">
                  Sandbox
                </span>
                <span className="text-xs text-amber-800">
                  Test mode - No real charges
                </span>
              </div>
            </div>
          )}
          
          {!sdkLoaded ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Loading PayPal...
            </div>
          ) : (
            <div>
              <div 
                id="paypal-button-container" 
                ref={paypalButtonsRef}
                className="min-h-[50px]"
              />
              {isProcessing && (
                <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing payment...
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="mt-6 pt-6 border-t border-primary/20 text-xs text-muted-foreground">
        <DollarSign className="w-4 h-4 inline mr-1" />
        Funds are added to your credit balance immediately after payment confirmation
      </div>
    </Card>
  );
}